import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import { RefreshCw, AlertTriangle, Info, ExternalLink } from "lucide-react";
import { functions } from "../firebase";
import useAdminToken from "../hooks/useAdminToken";
import { COUNTRIES } from "../utils/countries";
import { flagOf } from "../components/checkout-v2-editor";
/* THE SAME fees.js THE BACKEND USES — src/utils/fees.js is a symlink to
 * functions/fees.js, exactly as src/utils/countries.js is. Net-of-fees and the
 * fee rate are computed here by the identical code that computes them for the
 * sales dashboard's endpoint, so the two screens cannot quietly disagree about
 * what a sale was worth. Re-implementing pocket() in JSX would have been three
 * lines and a permanent source of "the dashboard says something else". */
import { pocket, rate as feeRate, STATUS } from "../utils/fees";
/* Same arrangement, same reason: Malaysian orders carry no country or gateway
 * field, and this screen having its own copy of that rule is precisely how it
 * came to disagree with the revenue endpoint — one said Malaysia, the other
 * said "unknown", for the same order. */
import { countryOf, gatewayOf } from "../utils/orders";
import { stripeUrl } from "../utils/stripe-dashboard";

/* Orders with what the payment platform kept.
 *
 * WHY THIS IS A SEPARATE SCREEN FROM THE ONE ON /admin/checkout. That table
 * predates the foreign markets and formats every amount as `RM ${x/100}` with
 * a single "Revenue" figure produced by adding every order's amount together.
 * That was right when every sale was in ringgit. It now renders HKD 7,800 as
 * "RM 7,800" and adds it to Malaysian sales, and the total it prints is not a
 * quantity of anything. It is left alone — it is the live admin for the live
 * v1 checkout — and nothing here repeats it:
 *
 *   · every amount is formatted with its OWN currency
 *   · every total is per-currency, and there is no all-currency sum anywhere
 *   · "net" means after the gateway's cut, which is the number that matters
 *
 * Reads go through the listOrders callable, so buyer contact details are never
 * readable from the browser without the admin key.
 */

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
  { label: "All time", days: null },
];

/* Money, always with its unit attached. Malaysia keeps the RM symbol it has
 * always used; everything else prints its ISO code, because SGD, AUD, BND and
 * HKD all render as a bare "$" and a revenue screen is the last place to leave
 * someone guessing which dollar they are looking at. Same rule as the
 * registry's formatAmount, which cannot be used directly here because these
 * amounts are keyed by currency rather than by country — a Stripe fee is in
 * MYR while its sale is in HKD. */
function fmt(minor, currency) {
  if (!Number.isInteger(minor)) return "—";
  const major = minor / 100;
  const body = major.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency === "MYR" ? `RM ${body}` : `${currency || "?"} ${body}`;
}

const when = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-MY", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const STATUS_CLASS = {
  captured: "badge-success",
  authorized: "badge-warning",
  failed: "badge-error",
};

const FEE_CLASS = {
  [STATUS.AVAILABLE]: "badge-ghost",
  [STATUS.PENDING]: "badge-warning",
  [STATUS.MISSING]: "badge-error",
};

export default function AdminCheckoutV2Orders() {
  const { token, verified, busy, verify, clear } = useAdminToken();
  const [keyInput, setKeyInput] = useState("");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rangeDays, setRangeDays] = useState(30);
  const [query, setQuery] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("captured");
  const [showTest, setShowTest] = useState(false);

  const load = useCallback(async () => {
    if (!verified) return;
    setLoading(true);
    try {
      const listOrders = httpsCallable(functions, "listOrders");
      const res = await listOrders({
        token,
        // 500, matching the table on /admin/checkout. That call is proven in
        // production; 1000 is listOrders' ceiling and was the one thing this
        // screen did differently, so it is not worth being the variable in a
        // 500 nobody has logs for yet. Raise it once the cause is known.
        limit: 500,
        sinceMs: rangeDays ? Date.now() - rangeDays * 86400000 : null,
      });
      const data = res.data || {};
      if (!data.ok) throw new Error(data.error || "Failed to load orders");
      // stats is deliberately ignored: its `revenue` is a cross-currency sum.
      setOrders(data.orders || []);
    } catch (e) {
      toast.error(String((e && e.message) || e));
    } finally {
      setLoading(false);
    }
  }, [token, rangeDays, verified]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return orders.filter((o) => {
      /* `live !== false`, never `live === true`. Malaysian orders come from the
         v1 webhook, which never writes the flag; testing for true would hide
         the home market entirely. */
      if (!showTest && o.live === false) return false;
      if (statusFilter !== "all" && o.status !== statusFilter) return false;
      if (countryFilter !== "all" && countryOf(o) !== countryFilter) return false;
      if (!q) return true;
      return [o.name, o.email, o.contact, o.promoCode, o.paymentId, o.orderId]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [orders, query, statusFilter, countryFilter, showTest]);

  /* Every total keyed by currency. There is no line in this function that adds
     two different currencies together, and there is no "grand total" for the
     same reason — one does not exist without an exchange rate we do not have. */
  const totals = useMemo(() => {
    const gross = {};
    const net = {};
    const unsettled = {};
    const fee = {};
    let missing = 0;
    let pending = 0;
    let grossMissing = 0;
    let netMissing = 0;

    for (const o of visible) {
      if (o.status !== "captured") continue;

      /* Counted when it cannot be read, never treated as zero. A real HitPay
         order was found with no usable amount: a captured sale that appeared
         in the row count while contributing nothing to its currency, so the
         total read low with nothing on screen admitting it. */
      const cur = o.currency || "?";
      if (Number.isInteger(o.amount)) gross[cur] = (gross[cur] || 0) + o.amount;
      else grossMissing++;

      const f = o.fee;
      if (!f || f.status === STATUS.MISSING) {
        missing++;
        continue;
      }
      if (f.status === STATUS.PENDING) pending++;

      if (Number.isInteger(f.amount)) fee[f.currency] = (fee[f.currency] || 0) + f.amount;

      const p = pocket(o);
      if (p && Number.isInteger(p.amount)) {
        // HitPay's net is Singapore dollars while the bank account is Malaysian,
        // and HitPay does not publish the rate it converts at. Kept in its own
        // column rather than added to the ringgit, which would silently assume
        // a rate of exactly 1.00.
        const bucket = p.settled ? net : unsettled;
        bucket[p.currency] = (bucket[p.currency] || 0) + p.amount;
      } else {
        /* Fee known, net not derivable — a PayNow sale, charged in SGD with the
           fee priced in MYR and no rate published between them. Its cost is in
           `fee` above and counted; only the net is absent. Tracked so the page
           can say so instead of the Net card quietly reading low. */
        netMissing++;
      }
    }

    const paidCount = visible.filter((o) => o.status === "captured").length;
    return { gross, net, unsettled, fee, missing, pending, grossMissing, netMissing, paidCount };
  }, [visible]);

  const handleVerify = async (e) => {
    e.preventDefault();
    try {
      await verify(keyInput);
      setKeyInput("");
      toast.success("Verified");
    } catch (err) {
      toast.error(String((err && err.message) || err));
    }
  };

  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card bg-base-100 shadow w-full max-w-md">
          <div className="card-body">
            <h3 className="font-bold text-lg">Admin Verification</h3>
            <p className="py-2 text-sm">Enter the admin key to view orders.</p>
            <form onSubmit={handleVerify}>
              <input
                type="password"
                className="input input-bordered w-full"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Admin key"
                autoFocus
              />
              <div className="modal-action">
                <button className="btn btn-primary" disabled={busy}>
                  {busy && <span className="loading loading-spinner loading-xs" />}
                  Verify
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold">
            Checkout v2 <span className="opacity-40">·</span> Orders
          </h1>
          <p className="text-sm opacity-70">
            Every market, net of what the payment platform kept. Prices and
            content are edited on{" "}
            <a className="link" href="/admin/checkout-v2">
              /admin/checkout-v2
            </a>
            .
          </p>
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <StripeQuickOpen />
          <button className="btn btn-outline btn-error btn-sm" onClick={clear}>
            Re-verify
          </button>
        </div>
      </div>

      {/* ---- Money, one card per currency ----
          Gross and net are separate cards rather than columns of one number,
          because there is no single number: four markets settle in ringgit and
          one does not. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-3">
        <MoneyCard
          label="Charged"
          hint="what buyers paid, in their currency"
          buckets={totals.gross}
        />
        <MoneyCard
          label="Platform fees"
          hint="what the gateway kept"
          buckets={totals.fee}
        />
        <MoneyCard
          label="Net received"
          hint="what reached the bank"
          buckets={totals.net}
          extra={totals.unsettled}
          extraHint="pending conversion"
          emphasis
        />
      </div>

      {/* ---- How much of the above can be trusted ----
          A net figure over partial coverage reads HIGH by exactly the fees that
          are missing from it. Saying so is the difference between a number and
          a misleading number. */}
      {totals.missing > 0 && (
        <div className="alert alert-warning mb-3 py-2">
          <AlertTriangle size={16} />
          <span className="text-sm">
            <b>{totals.missing}</b> of {totals.paidCount} captured orders have no
            fee recorded, so <b>Net received</b> is higher than the real figure by
            whatever those cost. Usually Stripe had not worked the fee out yet when
            the webhook fired — run{" "}
            <code>scripts/backfill-order-fees.js --gateway stripe --refetch</code>{" "}
            to fill them in.
          </span>
        </div>
      )}
      {totals.grossMissing > 0 && (
        <div className="alert alert-error mb-3 py-2">
          <AlertTriangle size={16} />
          <span className="text-sm">
            <b>{totals.grossMissing}</b> captured order
            {totals.grossMissing === 1 ? " has" : "s have"} no readable amount,
            so <b>Charged</b> is lower than the real figure. These need looking
            at in Firestore — a captured sale with no amount is a broken record,
            not a free one.
          </span>
        </div>
      )}
      {/* Not a fault, so not a warning. HitPay prices a PayNow charge in SGD
          and its fee in MYR and publishes no rate between them, so the cost is
          known and the net is not. Stated rather than left to be inferred from
          a Net card that does not add up to Charged minus fees. */}
      {totals.netMissing > 0 && (
        <div className="alert mb-3 py-2">
          <Info size={16} />
          <span className="text-sm">
            <b>{totals.netMissing}</b> PayNow sale
            {totals.netMissing === 1 ? "" : "s"} contribute to <b>Platform fees</b>{" "}
            but not to <b>Net received</b>: HitPay charges in SGD and prices its
            fee in MYR without publishing the rate between them, so the cost is
            known and the net cannot be worked out. Nothing to fix — it is how
            HitPay reports.
          </span>
        </div>
      )}
      {totals.pending > 0 && (
        <div className="text-xs opacity-60 mb-3">
          {totals.pending} order{totals.pending === 1 ? "" : "s"} with fees Stripe
          has calculated but not yet settled into the balance. The figures are
          final; the money has not landed.
        </div>
      )}

      {/* ---- Controls ---- */}
      <div className="flex gap-2 flex-wrap items-center mb-3">
        <input
          className="input input-bordered input-sm w-full sm:flex-1 sm:w-auto sm:min-w-[12rem]"
          placeholder="Search name, email, phone, order id…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="select select-bordered select-sm flex-1 sm:flex-none"
          value={countryFilter}
          onChange={(e) => setCountryFilter(e.target.value)}
        >
          <option value="all">All countries</option>
          {COUNTRIES.map((c) => (
            <option key={c.code} value={c.code}>
              {flagOf(c.code)} {c.label}
            </option>
          ))}
        </select>
        <select
          className="select select-bordered select-sm flex-1 sm:flex-none"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="captured">Captured</option>
          <option value="all">All statuses</option>
          <option value="authorized">Authorized</option>
          <option value="failed">Failed</option>
        </select>
        <select
          className="select select-bordered select-sm flex-1 sm:flex-none"
          value={String(rangeDays)}
          onChange={(e) =>
            setRangeDays(e.target.value === "null" ? null : Number(e.target.value))
          }
        >
          {RANGES.map((r) => (
            <option key={r.label} value={String(r.days)}>
              {r.label}
            </option>
          ))}
        </select>
        <label className="label cursor-pointer gap-2 py-0">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={showTest}
            onChange={(e) => setShowTest(e.target.checked)}
          />
          {/* The test checkout is public, so anyone can produce a captured
              4242 order. Off by default; counting one as revenue is fiction. */}
          <span className="label-text text-xs">Include test</span>
        </label>
        <button className="btn btn-sm btn-ghost" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-10">
          <span className="loading loading-spinner" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center p-10 opacity-60">
          No orders match these filters.
        </div>
      ) : (
        <>
          {/* Phone: a card per order. The desktop table has nine columns and
              scrolling one sideways hides the net, which is the column this
              screen exists for. */}
          <div className="space-y-2 sm:hidden">
            {visible.map((o) => (
              <OrderCard key={o.paymentId} o={o} />
            ))}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Buyer</th>
                  <th>Market</th>
                  <th className="text-right">Charged</th>
                  <th className="text-right">Fee</th>
                  <th className="text-right">Net</th>
                  <th>Method</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((o) => (
                  <OrderRow key={o.paymentId} o={o} />
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs opacity-50 mt-3">
            Showing {visible.length} of {orders.length} loaded. Malaysian orders
            come from the shared Curlec webhook, which serves both the current
            checkout and v2 — the two are not distinguishable here, and that is
            by design.
          </p>
        </>
      )}
    </div>
  );
}

/* One card, one currency per line. `buckets` is always an object keyed by
 * currency code and is never flattened into a single figure. */
function MoneyCard({ label, hint, buckets, extra, extraHint, emphasis }) {
  const entries = Object.entries(buckets || {}).filter(([, v]) => v !== 0);
  const extras = Object.entries(extra || {}).filter(([, v]) => v !== 0);

  return (
    <div className={`card bg-base-100 shadow ${emphasis ? "ring-1 ring-primary/30" : ""}`}>
      <div className="card-body p-4 gap-1">
        <div className="text-xs opacity-60">{label}</div>
        {entries.length === 0 && extras.length === 0 ? (
          <div className="text-xl font-bold opacity-40">—</div>
        ) : (
          entries.map(([cur, amount]) => (
            <div key={cur} className="text-xl font-bold whitespace-nowrap">
              {fmt(amount, cur)}
            </div>
          ))
        )}
        {extras.map(([cur, amount]) => (
          <div key={cur} className="text-sm opacity-70 whitespace-nowrap">
            + {fmt(amount, cur)}{" "}
            <span className="opacity-60">{extraHint}</span>
          </div>
        ))}
        {hint && <div className="text-xs opacity-50">{hint}</div>}
      </div>
    </div>
  );
}

/* The fee cell, which has to say three different things: a figure, a reason it
 * has no figure, or that the figure is not final. A blank cell would read as
 * "free". */
function FeeCell({ o, align = "text-right" }) {
  const f = o.fee;
  if (!f || f.status === STATUS.MISSING) {
    return (
      <div className={align}>
        <span className={`badge badge-sm ${FEE_CLASS[STATUS.MISSING]}`}>
          not recorded
        </span>
      </div>
    );
  }
  const r = feeRate(f);
  return (
    <div className={align}>
      <div className="whitespace-nowrap">{fmt(f.amount, f.currency)}</div>
      <div className="text-xs opacity-60">
        {r === null ? "" : `${(r * 100).toFixed(2)}%`}
        {f.status === STATUS.PENDING && " · pending"}
      </div>
    </div>
  );
}

function NetCell({ o, align = "text-right" }) {
  const p = pocket(o);
  if (!p) return <div className={`${align} opacity-40`}>—</div>;
  return (
    <div className={align}>
      <div className={`font-semibold whitespace-nowrap ${p.amount < 0 ? "text-error" : ""}`}>
        {fmt(p.amount, p.currency)}
      </div>
      {/* Not in the bank yet: HitPay reports in the charge currency. */}
      {!p.settled && <div className="text-xs opacity-60">unconverted</div>}
      {/* A refund does not return the fee, so a refunded sale nets negative.
          Saying so beside the number stops it reading as a bug. */}
      {Number.isInteger(p.refunded) && p.refunded > 0 && (
        <div className="text-xs opacity-60">after refund</div>
      )}
    </div>
  );
}

/* Paste any Stripe id — pi_, ch_, cus_, in_, … — and open it in the Dashboard.
 * Live mode only: test ids are rare here and every test order row already links
 * to its own /test/ page. Unknown prefixes fall through to Stripe's search. */
function StripeQuickOpen() {
  const [id, setId] = useState("");
  const open = (e) => {
    e.preventDefault();
    const url = stripeUrl(id);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <form className="join w-full sm:w-auto" onSubmit={open}>
      <input
        className="input input-bordered input-sm join-item w-full sm:w-56"
        placeholder="Stripe id: pi_, ch_, cus_…"
        value={id}
        onChange={(e) => setId(e.target.value)}
      />
      <button className="btn btn-sm join-item" type="submit" disabled={!id.trim()}>
        <ExternalLink size={14} /> Stripe
      </button>
    </form>
  );
}

function Market({ o }) {
  const code = countryOf(o);
  const c = COUNTRIES.find((x) => x.code === code);
  const gateway = gatewayOf(o);
  // Stripe orders are keyed by their PaymentIntent id, so the row knows its
  // own Dashboard page. Test orders open under /test/, where they live.
  const stripeLink =
    gateway === "stripe" && /^(pi|ch|py)_/.test(o.paymentId || "")
      ? stripeUrl(o.paymentId, { test: o.live === false })
      : null;
  return (
    <div className="whitespace-nowrap">
      <span aria-hidden="true">{code ? flagOf(code) : "🏳"}</span>{" "}
      {c ? c.label : code ? code.toUpperCase() : "—"}
      <div className="text-xs opacity-60">
        {stripeLink ? (
          <a
            className="link inline-flex items-center gap-1"
            href={stripeLink}
            target="_blank"
            rel="noopener noreferrer"
            title={`Open ${o.paymentId} in Stripe`}
          >
            stripe <ExternalLink size={11} />
          </a>
        ) : (
          gateway || "—"
        )}
      </div>
    </div>
  );
}

function OrderRow({ o }) {
  return (
    <tr>
      <td className="whitespace-nowrap">{when(o.capturedAt || o.createdAt)}</td>
      <td>
        <div className="font-medium">{o.name || "—"}</div>
        <div className="text-xs opacity-60">{o.email || "—"}</div>
        <div className="text-xs opacity-60">{o.contact || ""}</div>
      </td>
      <td>
        <Market o={o} />
      </td>
      <td className="text-right whitespace-nowrap">{fmt(o.amount, o.currency)}</td>
      <td>
        <FeeCell o={o} />
      </td>
      <td>
        <NetCell o={o} />
      </td>
      <td>
        {o.method || "—"}
        {(o.bank || o.wallet) && (
          <div className="text-xs opacity-60">{o.bank || o.wallet}</div>
        )}
      </td>
      <td>
        <span className={`badge badge-sm ${STATUS_CLASS[o.status] || "badge-ghost"}`}>
          {o.status || "—"}
        </span>
        {o.live === false && (
          <div className="text-xs opacity-60">test</div>
        )}
        {o.promoCode && <div className="text-xs opacity-60">{o.promoCode}</div>}
      </td>
    </tr>
  );
}

function OrderCard({ o }) {
  return (
    <div className="card bg-base-100 shadow">
      <div className="card-body p-3 gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-medium truncate">{o.name || "—"}</div>
            <div className="text-xs opacity-60 break-all">{o.email || "—"}</div>
            {o.contact && <div className="text-xs opacity-60">{o.contact}</div>}
          </div>
          <div className="text-right shrink-0">
            <div className="font-bold whitespace-nowrap">
              {fmt(o.amount, o.currency)}
            </div>
            <span className={`badge badge-sm ${STATUS_CLASS[o.status] || "badge-ghost"}`}>
              {o.status || "—"}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 border-t border-base-300 pt-2">
          <div>
            <div className="text-xs opacity-60">Fee</div>
            <FeeCell o={o} align="text-left" />
          </div>
          <div>
            <div className="text-xs opacity-60">Net</div>
            <NetCell o={o} align="text-left" />
          </div>
        </div>

        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs opacity-60">
          <span>{when(o.capturedAt || o.createdAt)}</span>
          <span>
            <Market o={o} />
          </span>
          <span>
            {o.method || "—"}
            {(o.bank || o.wallet) && ` · ${o.bank || o.wallet}`}
          </span>
          {o.promoCode && <span>Promo {o.promoCode}</span>}
          {o.live === false && <span>test</span>}
        </div>
      </div>
    </div>
  );
}
